import type { ComponentProps } from "react";
import { Callout as UICallout } from "ancestry-ui-react";
import { useUIComponentLoaded } from "@/shared/hooks/useUIComponentLoaded";

/**
 * Prop types mirror the real `ancestry-ui-react` Callout by deriving them directly
 * from it, so the wrapper stays a drop-in replacement and cannot drift from
 * `desc.d.ts`.
 */
export type CalloutProps = ComponentProps<typeof UICallout>;

/**
 * Safe boundary wrapper for `ancestry-ui-react`'s `Callout` (ADR-012 §3,
 * gate-the-mount).
 *
 * The underlying `Callout` reads `window.ui.callout` unconditionally in
 * `componentDidMount`, so mounting it before Ancestry's vanilla UI library
 * (`core.js`) has populated `window.ui` throws and surfaces as a `Main` FCI. We gate
 * the mount on the shared, hydration-safe readiness signal: render nothing until
 * `window.ui.callout` exists, then mount the real component.
 *
 * A closed callout paints no visible DOM, so gating introduces no layout shift; the
 * trigger element lives at the call site (outside this wrapper) and still renders.
 */
export const Callout = (props: CalloutProps) => {
    const isLoaded = useUIComponentLoaded("callout");

    if (!isLoaded) return null;

    return <UICallout {...props} />;
};
